import type {
  CompanySummary,
  CreateObraBody,
  ObraDetail,
  ReverseGeocode,
  SecretariaSummary,
} from '@obras/shared';
import {
  formatCurrency,
  maskCurrency,
  maskCnpj,
  OBRA_STATUS_LABELS,
  OBRA_STATUSES,
  parseCurrencyToCents,
} from '@obras/shared';
import type { LucideIcon } from 'lucide-react';
import {
  Building2,
  Check,
  ClipboardCheck,
  ClipboardList,
  FileText,
  Image as ImageIcon,
  Landmark,
  MapPin,
  Plus,
} from 'lucide-react';
import { lazy, Suspense, useMemo, useState, type FormEvent, type ReactNode } from 'react';

import { Button } from '../../components/ui/Button.js';
import { Checkbox, SelectField, TextAreaField, TextField } from '../../components/ui/Field.js';
import { FormError } from '../../components/ui/Feedback.js';
import { Modal } from '../../components/ui/Modal.js';
import { SingleSelect, type MultiSelectOption } from '../../components/ui/MultiSelect.js';
import { Tab, TabList } from '../../components/ui/Tabs.js';
import { api, ApiError } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { CompanyFormModal } from '../empresas/CompanyFormModal.js';
import { SecretariaFormModal } from '../secretarias/SecretariaFormModal.js';
import { useSecretariaOptions } from '../secretarias/api.js';
import { CapaDaObra } from './CapaDaObra.js';
import { useOrgMembers } from '../usuarios/api.js';
import { useCompanyOptions, useCreateObra, useUpdateObra } from './api.js';

const ICON = { strokeWidth: 1.5 } as const;

/**
 * O mapa entra por carregamento tardio.
 *
 * Leaflet e a folha de estilo dele somam uns quarenta e cinco kilobytes que só
 * fazem falta dentro deste formulário, na última etapa. Embutidos no pacote
 * principal, seriam baixados por toda pessoa que abre o sistema para olhar a
 * lista de obras — em rede de cidade pequena isso é meio segundo de espera para
 * quem nunca vai cadastrar nada.
 */
const MapaLocal = lazy(async () => ({
  default: (await import('../../components/ui/MapaLocal.js')).MapaLocal,
}));

interface FormState {
  name: string;
  description: string;
  contractNumber: string;
  initialValue: string;
  status: string;
  isReform: boolean;
  secretariaId: string;
  companyId: string;
  responsibleFiscalId: string;
  contractStart: string;
  contractEnd: string;
  osStart: string;
  osEnd: string;
  visitIntervalDays: string;
  coverMediaId: string;
  latitude: string;
  longitude: string;
  address: string;
  city: string;
  state: string;
}

function initialState(obra?: ObraDetail, fiscalPadrao?: string): FormState {
  return {
    name: obra?.name ?? '',
    description: obra?.description ?? '',
    contractNumber: obra?.contractNumber ?? '',
    initialValue: obra ? formatCurrency(obra.initialValueCents) : '',
    status: obra?.status ?? 'em_andamento',
    isReform: obra?.isReform ?? false,
    secretariaId: obra?.secretaria?.id ?? '',
    companyId: obra?.company?.id ?? '',
    responsibleFiscalId: obra?.responsibleFiscal?.id ?? fiscalPadrao ?? '',
    contractStart: obra?.contractStart ?? '',
    contractEnd: obra?.contractEnd ?? '',
    osStart: obra?.osStart ?? '',
    osEnd: obra?.osEnd ?? '',
    visitIntervalDays: obra ? String(obra.visitIntervalDays) : '',
    coverMediaId: obra?.coverMediaId ?? '',
    latitude: obra?.latitude != null ? String(obra.latitude) : '',
    longitude: obra?.longitude != null ? String(obra.longitude) : '',
    address: obra?.address ?? '',
    city: obra?.city ?? '',
    state: obra?.state ?? '',
  };
}

/** Campo vazio vira `undefined`: o backend distingue "não informado" de "vazio". */
const orUndefined = (value: string): string | undefined =>
  value.trim() === '' ? undefined : value;

const numberOrUndefined = (value: string): number | undefined => {
  const trimmed = value.trim().replace(',', '.');
  if (trimmed === '') return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/** Coordenada digitada à mão, para o mapa acompanhar. `NaN` não vira ponto. */
const coordOrNull = (value: string): number | null => {
  const parsed = Number(value.trim().replace(',', '.'));
  return value.trim() !== '' && Number.isFinite(parsed) ? parsed : null;
};

// ──────────────────────────────────────────────────────────── etapas

type EtapaId = 'identificacao' | 'capa' | 'empresa' | 'contrato' | 'fiscalizacao' | 'localizacao';

interface Etapa {
  readonly id: EtapaId;
  readonly rotulo: string;
  readonly icone: LucideIcon;
  readonly resumo: string;
  /** Campos do servidor que pertencem a esta etapa, para marcar o erro nela. */
  readonly campos: readonly string[];
}

const ETAPAS: readonly Etapa[] = [
  {
    id: 'identificacao',
    rotulo: 'Identificação',
    icone: ClipboardList,
    resumo: 'Denominação, objeto contratual, demandante e situação da execução.',
    campos: ['name', 'description', 'status', 'isReform', 'secretariaId'],
  },
  {
    id: 'capa',
    rotulo: 'Capa',
    icone: ImageIcon,
    resumo: 'Imagem que representa a obra nas listagens e na ficha.',
    campos: ['coverMediaId'],
  },
  {
    id: 'empresa',
    rotulo: 'Empresa',
    icone: Building2,
    resumo: 'Pessoa jurídica responsável pela execução do objeto.',
    campos: ['companyId'],
  },
  {
    id: 'contrato',
    rotulo: 'Contrato',
    icone: FileText,
    resumo: 'Identificação do instrumento, valor pactuado e vigências.',
    campos: [
      'contractNumber',
      'initialValueCents',
      'contractStart',
      'contractEnd',
      'osStart',
      'osEnd',
    ],
  },
  {
    id: 'fiscalizacao',
    rotulo: 'Fiscalização',
    icone: ClipboardCheck,
    resumo: 'Servidor designado e periodicidade das vistorias técnicas.',
    campos: ['visitIntervalDays', 'responsibleFiscalId'],
  },
  {
    id: 'localizacao',
    rotulo: 'Localização',
    icone: MapPin,
    resumo: 'Coordenadas geográficas e endereço do local da obra.',
    campos: ['latitude', 'longitude', 'address', 'city', 'state'],
  },
];

/**
 * Cadastro e edição de obra, em etapas.
 *
 * Eram dezoito campos num rolo só, e a pessoa terminava sem saber o que tinha
 * pulado. Divididos em cinco etapas com a trilha à esquerda, cada tela responde
 * a uma pergunta e a trilha mostra onde ela está — inclusive quando o servidor
 * recusa: a etapa com campo errado ganha um ponto vermelho, e o formulário salta
 * para ela.
 *
 * **Nenhuma etapa tranca a seguinte.** É um formulário longo com sumário, não um
 * assistente: quem já sabe o número do contrato pode ir direto nele, e só o nome
 * da obra é obrigatório.
 */
export function ObraFormModal({
  open,
  onClose,
  obra,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  obra?: ObraDetail;
  onSaved?: (obra: ObraDetail) => void;
}) {
  const { user, can, may } = useAuth();

  /*
   * Fiscal comum não escolhe o responsável: a obra nasce dele. Poder apontar
   * outra pessoa seria atribuir trabalho a um colega, que é decisão de gestão —
   * e o servidor recusaria de qualquer forma.
   *
   * Abaixo de fiscal a obra nasce sem responsável (ADR 044): o leitor cadastra,
   * mas não fiscaliza, e apontá-lo como responsável o faria aparecer no filtro
   * por fiscal sob uma obra que nunca visitará.
   */
  const podeDesignar = can('gestor');
  const assumeResponsabilidade = !podeDesignar && can('fiscal');
  const responsavelExibido =
    obra?.responsibleFiscal?.name ?? (assumeResponsabilidade ? user?.name : undefined);
  const notaDoResponsavel = obra?.responsibleFiscal
    ? 'A transferência de titularidade compete à gestão.'
    : assumeResponsabilidade
      ? 'A obra será registrada sob sua responsabilidade. A transferência de titularidade compete à gestão.'
      : 'A designação do fiscal responsável compete à gestão.';

  /*
   * Cadastrar empresa e secretaria são permissões próprias. Quem não alcança
   * não recebe o botão: o servidor recusaria, e o formulário de obra não é o
   * lugar de descobrir isso.
   */
  const podeCadastrarEmpresa = may('canManageCompanies');
  const podeCadastrarSecretaria = may('canAccessSecretarias');

  const [form, setForm] = useState<FormState>(() =>
    initialState(obra, assumeResponsabilidade ? user?.id : undefined),
  );
  const [etapa, setEtapa] = useState<EtapaId>('identificacao');

  /*
   * A capa vive em dois lugares: o `coverMediaId` vai no corpo da requisição, e
   * a URL serve só para a miniatura na tela. Guardá-las juntas evita uma ida ao
   * servidor só para saber que imagem o id representa.
   *
   * Na edição, `coverUrl` só é aproveitado quando a obra tem capa **própria**:
   * sem `coverMediaId` a URL é a de uma foto de relatório, herdada para o cartão
   * não ficar vazio, e exibi-la aqui faria o formulário prometer uma capa que
   * não existe.
   */
  const [capa, setCapa] = useState<{ mediaId: string; url: string } | null>(() =>
    obra?.coverMediaId && obra.coverUrl ? { mediaId: obra.coverMediaId, url: obra.coverUrl } : null,
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [novaEmpresa, setNovaEmpresa] = useState(false);
  const [novaSecretaria, setNovaSecretaria] = useState(false);
  const [buscandoEndereco, setBuscandoEndereco] = useState(false);

  const create = useCreateObra();
  const update = useUpdateObra(obra?.id ?? '');
  const members = useOrgMembers();
  const companies = useCompanyOptions();
  const secretarias = useSecretariaOptions();

  const saving = create.isPending || update.isPending;

  const set = <K extends keyof FormState>(key: K, value: FormState[K]): void =>
    setForm((current) => ({ ...current, [key]: value }));

  const opcoesDeEmpresa: MultiSelectOption[] = useMemo(
    () =>
      (companies.data?.items ?? []).map((empresa) => ({
        value: empresa.id,
        label: empresa.tradeName ?? empresa.legalName,
        detail: empresa.tradeName ? empresa.legalName : (empresa.cnpj ?? undefined),
        // Achar pelo CNPJ: ninguém o decora, mas está impresso no contrato.
        terms: empresa.cnpj ? [empresa.cnpj, maskCnpj(empresa.cnpj)] : undefined,
      })),
    [companies.data],
  );

  const opcoesDeSecretaria: MultiSelectOption[] = useMemo(
    () =>
      (secretarias.data?.items ?? []).map((item) => ({
        value: item.id,
        label: item.acronym ? `${item.acronym} — ${item.name}` : item.name,
        detail: item.description ?? undefined,
        terms: item.acronym ? [item.acronym] : undefined,
      })),
    [secretarias.data],
  );

  const opcoesDeFiscal: MultiSelectOption[] = useMemo(
    () =>
      (members.data?.items ?? []).map((membro) => ({
        value: membro.id,
        label: membro.name,
        detail: membro.email ?? undefined,
      })),
    [members.data],
  );

  /**
   * O que impede o salvamento, por etapa.
   *
   * A mesma regra que o servidor aplica na criação, adiantada para a tela: sem
   * contrato não há o que fiscalizar, e sem valor o livro-razão nasce sem base
   * de comparação. Deixar descobrir isso só depois de clicar em "Criar obra",
   * numa etapa que já saiu de vista, é o pior momento para avisar.
   *
   * Vale só na criação. Na edição o `PATCH` é parcial, e travar o botão numa
   * obra migrada sem valor impediria de corrigir justamente o resto dela.
   */
  const pendencias = useMemo(() => {
    if (obra) return new Map<EtapaId, string[]>();

    const faltando = new Map<EtapaId, string[]>();
    const anotar = (id: EtapaId, campo: string): void => {
      faltando.set(id, [...(faltando.get(id) ?? []), campo]);
    };

    if (form.name.trim().length < 3) anotar('identificacao', 'Denominação da obra');
    if (form.contractNumber.trim() === '') anotar('contrato', 'Número do contrato');
    if ((parseCurrencyToCents(form.initialValue) ?? 0) <= 0) {
      anotar('contrato', 'Valor inicial contratado');
    }

    return faltando;
  }, [obra, form.name, form.contractNumber, form.initialValue]);

  const faltaAlgo = pendencias.size > 0;

  /** Quais etapas têm campo recusado pelo servidor. */
  const etapasComErro = useMemo(() => {
    const comErro = new Set<EtapaId>();
    for (const passo of ETAPAS) {
      if (passo.campos.some((campo) => fieldErrors[campo])) comErro.add(passo.id);
    }
    return comErro;
  }, [fieldErrors]);

  /**
   * Traz logradouro, município e UF do ponto marcado.
   *
   * **Só preenche o que está em branco.** O endereço que veio do projeto é mais
   * preciso que o do OpenStreetMap, e quem já o digitou não pode perdê-lo por
   * ter ajustado o pino depois.
   *
   * Falha em silêncio: é uma comodidade, e os três campos continuam ali para
   * serem preenchidos à mão. Um alerta vermelho porque o Nominatim não respondeu
   * assustaria sem motivo.
   */
  async function preencherEndereco(lat: number, lng: number): Promise<void> {
    setBuscandoEndereco(true);

    try {
      const achado = await api.get<ReverseGeocode>(
        `/obras/endereco-por-coordenada?lat=${lat}&lng=${lng}`,
      );

      setForm((atualForm) => ({
        ...atualForm,
        address: atualForm.address || (achado.address ?? ''),
        city: atualForm.city || (achado.city ?? ''),
        state: atualForm.state || (achado.state ?? ''),
      }));
    } catch {
      // Silêncio proposital; ver o comentário acima.
    } finally {
      setBuscandoEndereco(false);
    }
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFieldErrors({});
    setFormError(null);

    const body: CreateObraBody = {
      name: form.name.trim(),
      description: orUndefined(form.description),
      isReform: form.isReform,
      secretariaId: orUndefined(form.secretariaId),
      contractNumber: form.contractNumber.trim(),
      companyId: orUndefined(form.companyId),
      responsibleFiscalId: orUndefined(form.responsibleFiscalId),
      status: form.status as CreateObraBody['status'],
      initialValueCents: parseCurrencyToCents(form.initialValue) ?? 0,
      contractStart: orUndefined(form.contractStart),
      contractEnd: orUndefined(form.contractEnd),
      osStart: orUndefined(form.osStart),
      osEnd: orUndefined(form.osEnd),
      visitIntervalDays: numberOrUndefined(form.visitIntervalDays),
      coverMediaId: orUndefined(form.coverMediaId),
      latitude: numberOrUndefined(form.latitude),
      longitude: numberOrUndefined(form.longitude),
      address: orUndefined(form.address),
      city: orUndefined(form.city),
      state: orUndefined(form.state),
    };

    try {
      const saved = obra ? await update.mutateAsync(body) : await create.mutateAsync(body);

      onSaved?.(saved);
      onClose();
      if (!obra) {
        setForm(initialState(undefined, podeDesignar ? undefined : user?.id));
        setCapa(null);
        setEtapa('identificacao');
      }
    } catch (error) {
      if (error instanceof ApiError) {
        const campos = error.fieldErrors();
        setFieldErrors(campos);
        setFormError(error.details ? 'Verifique os campos destacados.' : error.message);

        /*
         * Salta para a primeira etapa com erro. Sem isso, a mensagem falaria de
         * um campo que não está na tela — e o formulário pareceria travado.
         */
        const primeira = ETAPAS.find((passo) => passo.campos.some((campo) => campos[campo]));
        if (primeira) setEtapa(primeira.id);
      } else {
        setFormError('Não foi possível salvar. Tente novamente.');
      }
    }
  }

  const atual = ETAPAS.find((passo) => passo.id === etapa) ?? ETAPAS[0]!;
  const erro = (campo: string) => (fieldErrors[campo] ? { error: fieldErrors[campo] } : {});

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        size="xl"
        flush
        title={obra ? 'Editar obra' : 'Nova obra'}
        description="Dados do contrato, prazos, responsáveis e localização."
        footer={
          <>
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              form="obra-form"
              type="submit"
              loading={saving}
              disabled={faltaAlgo}
              /*
               * O botão desabilitado precisa dizer o que falta. Um botão cinza
               * sem explicação é um beco: a pessoa clica, nada acontece, e não
               * há como saber por quê.
               */
              title={
                faltaAlgo
                  ? `Preencha antes: ${[...pendencias.values()].flat().join(', ')}`
                  : undefined
              }
              icon={<Check {...ICON} size={16} />}
            >
              {obra ? 'Salvar alterações' : 'Criar obra'}
            </Button>
          </>
        }
      >
        {/*
         * Altura **fixa** a partir de `sm`, com a rolagem na coluna do
         * formulário.
         *
         * Mínima não bastava: com um piso de 500px o modal ainda ia a 795px na
         * Identificação e a 808px na Localização, porque o conteúdo passa do
         * piso. O rodapé subia e descia debaixo do ponteiro a cada troca de
         * etapa. Fixando a altura, quem rola é a coluna da direita e o modal
         * não se mexe.
         *
         * O `max-h-[85dvh]` do modal continua mandando: em tela baixa o corpo
         * rola por fora e a altura para de crescer — constante do mesmo jeito.
         *
         * No celular a altura fica livre. O modal já é uma folha que sobe do pé
         * da tela, e prender 520px numa etapa de dois campos deixaria metade da
         * folha vazia com uma barra de rolagem inútil ao lado.
         */}
        <div className="grid gap-6 px-6 pb-2 sm:h-[520px] sm:grid-cols-[196px_1fr]">
          {/*
           * A trilha rola junto em tela estreita, e vira coluna fixa a partir
           * de `sm`. Empilhada no celular ela fica no topo, que é onde um
           * sumário é procurado.
           */}
          <nav className="sm:sticky sm:top-0 sm:self-start">
            <TabList label="Etapas do cadastro" orientation="vertical">
              {ETAPAS.map((passo) => (
                <Tab
                  key={passo.id}
                  vertical
                  active={etapa === passo.id}
                  // O ponto marca tanto o que o servidor recusou quanto o que
                  // ainda falta para o botão de salvar liberar.
                  alert={etapasComErro.has(passo.id) || pendencias.has(passo.id)}
                  icon={passo.icone}
                  onClick={() => setEtapa(passo.id)}
                >
                  {passo.rotulo}
                </Tab>
              ))}
            </TabList>
          </nav>

          <form
            id="obra-form"
            onSubmit={(event) => void handleSubmit(event)}
            /* `pr-1` afasta o conteúdo da barra de rolagem própria da coluna. */
            className="flex min-w-0 flex-col gap-5 sm:overflow-y-auto sm:pr-1"
            noValidate
          >
            <header>
              <h3 className="text-[15px] font-semibold tracking-tight">{atual.rotulo}</h3>
              <p className="mt-0.5 text-[13px] text-muted-foreground">{atual.resumo}</p>
            </header>

            {formError ? <FormError>{formError}</FormError> : null}

            {etapa === 'identificacao' ? (
              <Painel>
                <TextField
                  label="Denominação da obra"
                  required
                  autoFocus
                  placeholder="Construção da Unidade Básica de Saúde do Bairro Jardim"
                  hint="Como a obra será identificada em relatórios e listagens."
                  value={form.name}
                  onChange={(event) => set('name', event.target.value)}
                  {...erro('name')}
                />

                <TextAreaField
                  label="Objeto"
                  rows={4}
                  hint="Transcreva o objeto conforme redigido no instrumento contratual."
                  value={form.description}
                  onChange={(event) => set('description', event.target.value)}
                  {...erro('description')}
                />

                <SelectField
                  label="Situação da execução"
                  value={form.status}
                  onChange={(event) => set('status', event.target.value)}
                  {...erro('status')}
                >
                  {OBRA_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {OBRA_STATUS_LABELS[status]}
                    </option>
                  ))}
                </SelectField>

                <Checkbox
                  label="Reforma ou ampliação de edificação existente"
                  hint="Distingue a intervenção em edificação existente da construção nova nos relatórios gerenciais."
                  checked={form.isReform}
                  onChange={(event) => set('isReform', event.target.checked)}
                />

                {/*
                 * A secretaria demandante fica ao lado da situação: as duas
                 * respondem "de quem é esta obra e como ela está", e é assim que
                 * a ficha da obra as apresenta depois.
                 */}
                <div>
                  <span className="mb-1.5 block text-[13px] font-medium">
                    Secretaria demandante
                  </span>
                  <SingleSelect
                    label="Secretaria demandante"
                    searchable
                    placeholder={secretarias.isLoading ? 'Carregando…' : 'Buscar por nome ou sigla'}
                    options={opcoesDeSecretaria}
                    value={form.secretariaId || null}
                    onChange={(value) => set('secretariaId', value)}
                  />
                  <p className="mt-1.5 text-[12px] text-muted-foreground">
                    Órgão interno que solicitou a obra e responde por ela.
                  </p>
                  {fieldErrors['secretariaId'] ? (
                    <p className="mt-1.5 text-[12px] text-danger">{fieldErrors['secretariaId']}</p>
                  ) : null}
                </div>

                <div className="flex flex-wrap gap-2">
                  {podeCadastrarSecretaria ? (
                    <Button
                      size="sm"
                      icon={<Landmark {...ICON} size={14} />}
                      onClick={() => setNovaSecretaria(true)}
                    >
                      Cadastrar secretaria
                    </Button>
                  ) : null}
                  {form.secretariaId ? (
                    <Button size="sm" variant="ghost" onClick={() => set('secretariaId', '')}>
                      Limpar seleção
                    </Button>
                  ) : null}
                </div>
              </Painel>
            ) : null}

            {etapa === 'capa' ? (
              <Painel>
                <CapaDaObra
                  {...(obra ? { obraId: obra.id } : {})}
                  atual={capa}
                  onChange={(escolhida) => {
                    setCapa(escolhida);
                    set('coverMediaId', escolhida?.mediaId ?? '');
                  }}
                />
              </Painel>
            ) : null}

            {etapa === 'empresa' ? (
              <Painel>
                <div>
                  <span className="mb-1.5 block text-[13px] font-medium">Empresa contratada</span>
                  <SingleSelect
                    label="Empresa contratada"
                    searchable
                    placeholder={
                      companies.isLoading ? 'Carregando empresas…' : 'Buscar por nome ou CNPJ'
                    }
                    options={opcoesDeEmpresa}
                    value={form.companyId || null}
                    onChange={(value) => set('companyId', value)}
                  />
                  {fieldErrors['companyId'] ? (
                    <p className="mt-1.5 text-[12px] text-danger">{fieldErrors['companyId']}</p>
                  ) : null}
                </div>

                {/*
                 * Cadastrar a empresa daqui, e não em outra tela: a licitação
                 * termina e a obra é cadastrada no mesmo dia, com a empresa
                 * ainda fora do sistema. Sair para cadastrá-la perderia o que já
                 * foi preenchido.
                 */}
                <div className="rounded-md bg-muted px-4 py-3.5">
                  <p className="text-[13px] font-medium">Empresa ainda não cadastrada</p>
                  {podeCadastrarEmpresa ? (
                    <>
                      <p className="mt-0.5 text-[13px] text-muted-foreground">
                        Efetue o cadastro sem sair deste formulário. A partir do CNPJ, o sistema
                        recupera razão social, endereço e telefone na base da Receita Federal.
                      </p>
                      <Button
                        size="sm"
                        variant="surface"
                        className="mt-3"
                        icon={<Plus {...ICON} size={14} />}
                        onClick={() => setNovaEmpresa(true)}
                      >
                        Cadastrar nova empresa
                      </Button>
                    </>
                  ) : (
                    <p className="mt-0.5 text-[13px] text-muted-foreground">
                      O cadastro da empresa contratada depende do acesso "Gerenciar empresas". A
                      obra pode ser registrada agora e vinculada à empresa depois.
                    </p>
                  )}
                </div>

                {form.companyId ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="self-start"
                    onClick={() => set('companyId', '')}
                  >
                    Limpar seleção
                  </Button>
                ) : null}
              </Painel>
            ) : null}

            {etapa === 'contrato' ? (
              <Painel>
                <div className="grid gap-4 sm:grid-cols-2">
                  <TextField
                    label="Número do contrato"
                    mono
                    placeholder="056/2026"
                    value={form.contractNumber}
                    onChange={(event) => set('contractNumber', event.target.value)}
                    {...erro('contractNumber')}
                  />

                  {/*
                   * A máscara entra a cada tecla, da direita para a esquerda.
                   * Formatar só ao sair do campo deixava o número ambíguo
                   * justamente enquanto a pessoa o conferia.
                   */}
                  <TextField
                    label="Valor inicial contratado"
                    mono
                    inputMode="numeric"
                    placeholder="R$ 0,00"
                    hint="Aditivos e supressões são registrados por relatório."
                    value={form.initialValue}
                    onChange={(event) => set('initialValue', maskCurrency(event.target.value))}
                    {...erro('initialValueCents')}
                  />
                </div>

                <div>
                  <h4 className="mb-3 text-[11px] font-medium tracking-[0.04em] text-muted-foreground uppercase">
                    Vigência do contrato
                  </h4>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <TextField
                      label="Assinatura"
                      type="date"
                      mono
                      value={form.contractStart}
                      onChange={(event) => set('contractStart', event.target.value)}
                      {...erro('contractStart')}
                    />
                    <TextField
                      label="Encerramento"
                      type="date"
                      mono
                      value={form.contractEnd}
                      onChange={(event) => set('contractEnd', event.target.value)}
                      {...erro('contractEnd')}
                    />
                  </div>
                </div>

                <div>
                  <h4 className="mb-3 text-[11px] font-medium tracking-[0.04em] text-muted-foreground uppercase">
                    Ordem de serviço
                  </h4>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <TextField
                      label="Emissão"
                      type="date"
                      mono
                      hint="Marco inicial da contagem do prazo de execução."
                      value={form.osStart}
                      onChange={(event) => set('osStart', event.target.value)}
                      {...erro('osStart')}
                    />
                    <TextField
                      label="Término previsto"
                      type="date"
                      mono
                      value={form.osEnd}
                      onChange={(event) => set('osEnd', event.target.value)}
                      {...erro('osEnd')}
                    />
                  </div>
                </div>
              </Painel>
            ) : null}

            {etapa === 'fiscalizacao' ? (
              <Painel>
                <div className="grid gap-4 sm:grid-cols-2">
                  <TextField
                    label="Periodicidade das vistorias"
                    type="number"
                    min={1}
                    max={365}
                    mono
                    placeholder="15"
                    hint="Intervalo em dias. Não informado, aplica-se o padrão do órgão."
                    value={form.visitIntervalDays}
                    onChange={(event) => set('visitIntervalDays', event.target.value)}
                    {...erro('visitIntervalDays')}
                  />

                  {podeDesignar ? (
                    <div>
                      <span className="mb-1.5 block text-[13px] font-medium">
                        Fiscal responsável
                      </span>
                      <SingleSelect
                        label="Fiscal responsável"
                        searchable
                        placeholder={
                          members.isLoading ? 'Carregando…' : 'Nenhum servidor designado'
                        }
                        options={opcoesDeFiscal}
                        value={form.responsibleFiscalId || null}
                        onChange={(value) => set('responsibleFiscalId', value)}
                      />
                      {fieldErrors['responsibleFiscalId'] ? (
                        <p className="mt-1.5 text-[12px] text-danger">
                          {fieldErrors['responsibleFiscalId']}
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <div>
                      <span className="mb-1.5 block text-[13px] font-medium">
                        Fiscal responsável
                      </span>
                      <p className="flex h-10 items-center rounded-sm bg-muted px-3 text-[13px]">
                        {responsavelExibido ?? 'Nenhum fiscal designado'}
                      </p>
                      <p className="mt-1.5 text-[12px] text-muted-foreground">
                        {notaDoResponsavel}
                      </p>
                    </div>
                  )}
                </div>
              </Painel>
            ) : null}

            {etapa === 'localizacao' ? (
              <Painel>
                <div>
                  <Suspense
                    fallback={
                      <div className="grid h-80 w-full place-items-center rounded-md bg-muted text-[13px] text-muted-foreground">
                        Carregando o mapa…
                      </div>
                    }
                  >
                    <MapaLocal
                      latitude={coordOrNull(form.latitude)}
                      longitude={coordOrNull(form.longitude)}
                      onChange={(lat, lng) => {
                        setForm((atualForm) => ({
                          ...atualForm,
                          latitude: String(lat),
                          longitude: String(lng),
                        }));
                        void preencherEndereco(lat, lng);
                      }}
                    />
                  </Suspense>

                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <TextField
                      label="Latitude"
                      mono
                      inputMode="decimal"
                      placeholder="-10.178211"
                      hint="Preenchida pela marcação no mapa ou informada diretamente."
                      value={form.latitude}
                      onChange={(event) => set('latitude', event.target.value)}
                      {...erro('latitude')}
                    />
                    <TextField
                      label="Longitude"
                      mono
                      inputMode="decimal"
                      placeholder="-59.445292"
                      value={form.longitude}
                      onChange={(event) => set('longitude', event.target.value)}
                      {...erro('longitude')}
                    />
                  </div>

                  <div className="mt-4 grid gap-4">
                    <TextField
                      label="Logradouro"
                      placeholder="Avenida Ari Mendes da Silva, s/nº"
                      hint={
                        buscandoEndereco
                          ? 'Consultando o endereço do ponto marcado…'
                          : 'Preenchido a partir do mapa quando o campo está vazio.'
                      }
                      value={form.address}
                      onChange={(event) => set('address', event.target.value)}
                      {...erro('address')}
                    />

                    <div className="grid gap-4 sm:grid-cols-[1fr_120px]">
                      <TextField
                        label="Município"
                        value={form.city}
                        onChange={(event) => set('city', event.target.value)}
                        {...erro('city')}
                      />
                      <TextField
                        label="UF"
                        maxLength={2}
                        mono
                        placeholder="MT"
                        value={form.state}
                        onChange={(event) => set('state', event.target.value.toUpperCase())}
                        {...erro('state')}
                      />
                    </div>
                  </div>
                </div>
              </Painel>
            ) : null}
          </form>
        </div>
      </Modal>

      {/*
       * Um `<dialog>` dentro de outro: os dois vão para a camada superior do
       * navegador, e o de cima recebe o foco. Fechá-lo devolve o foco ao
       * formulário da obra, com a empresa recém-criada já selecionada.
       */}
      <CompanyFormModal
        open={novaEmpresa}
        onClose={() => setNovaEmpresa(false)}
        onSaved={(empresa: CompanySummary) => set('companyId', empresa.id)}
      />

      <SecretariaFormModal
        open={novaSecretaria}
        onClose={() => setNovaSecretaria(false)}
        onSaved={(criada: SecretariaSummary) => set('secretariaId', criada.id)}
      />
    </>
  );
}

/** O corpo de uma etapa. Espaçamento único, para as quatro não divergirem. */
function Painel({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-4">{children}</div>;
}
